<style lang="scss" scoped>
.dashboard {
  padding: 8px 0;
  font-family: 'Segoe UI', sans-serif;
}

/* ── Section header ── */
.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;

  h2 {
    font-size: 15px;
    font-weight: 700;
    color: #0d1b3e;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 8px;

    &::before {
      content: '';
      display: inline-block;
      width: 3px;
      height: 16px;
      background: #F58020;
      border-radius: 2px;
    }
  }
}

/* ── Stat cards row ── */
.stat-cards-row {
  margin-bottom: 28px;
}

/* ── Panel card ── */
.panel-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(13, 27, 62, 0.07);
  margin-bottom: 24px;
  overflow: hidden;

  .panel-card__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 20px;
    background: #0d1b3e;
    color: #fff;

    .panel-card__title {
      font-size: 13px;
      font-weight: 600;
      letter-spacing: 0.3px;
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .panel-card__badge {
      font-size: 11px;
      background: rgba(255, 255, 255, 0.15);
      border-radius: 20px;
      padding: 2px 10px;
    }

    &.light {
      background: #d2e5f8;
      color: #389ad4;
      border-bottom: 1px solid #dde4f5;
    }
  }

  .panel-card__body {
    padding: 20px;
  }
}

/* ── Submission table ── */
.submission-table {
  width: 100%;
  font-size: 13px;

  thead tr th {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #7a8499;
    padding: 0 8px 10px;
    border-bottom: 2px solid #eef0f8;
  }

  tbody tr {
    border-bottom: 1px solid #f5f7fa;
    transition: background 0.15s;

    &:last-child {
      border-bottom: none;
    }

    &:hover {
      background: #f8f9ff;
    }

    td {
      padding: 9px 8px;
      color: #2d3748;
    }
  }

  .cell-center {
    text-align: center;
  }

  .cell-link {
    color: #389ad4;
    cursor: pointer;
    font-weight: 600;

    &:hover {
      text-decoration: underline;
    }
  }

  .cell-muted {
    color: #a0aab8;
  }

  .cell-error {
    color: #c62828;
    font-weight: 600;
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }
}

/* ── Appointment panel ── */
.appt-panel--full {
  width: 100%;
  overflow-y: auto;
  max-height: none;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: #f5f7fa;
  }

  &::-webkit-scrollbar-thumb {
    background: #c5cfe0;
    border-radius: 4px;
  }

  .table thead th {
    position: sticky;
    top: 0;
    z-index: 2;
    background: #fff;
    box-shadow: 0 1px 0 #e8edf5;
  }
}

/* ── Chart filters ── */
.chart-filters {
  display: flex;
  gap: 8px;
  margin-bottom: 16px;

  .filter-btn {
    padding: 6px 16px;
    font-size: 13px;
    font-weight: 600;
    border: 1.5px solid #dde2ec;
    border-radius: 8px;
    background: #fff;
    color: #4a5568;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
      border-color: #389ad4;
      color: #389ad4;
    }

    &.active {
      background: #F58020;
      border-color: #F58020;
      color: #fff;
    }
  }
}
</style>

<style>
.daterangepicker .calendars-container {
  margin-left: 20% !important;
  margin-top: -43% !important;
}

/* Notification modal fullscreen */
.fullscreen-modal .modal-dialog {
  max-width: 100%;
  width: 100%;
  margin: 0;
  height: 100%;
}

.fullscreen-modal .modal-content {
  height: 100vh;
  width: 100vw;
  border-radius: 0;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 20px;
}
</style>

<template>
  <div class="dashboard">

    <!-- Notification Modal -->
    <b-modal id="notification-detail-modal" v-model="showNotificationModal" size="xl" centered no-close-on-backdrop
      hide-header-close modal-class="fullscreen-modal">
      <template #modal-title>
        <div class="d-flex align-items-center gap-2">
          <i class="fa fa-bell mr-2"></i>
          <span
            :class="currentNotification.is_urgent ? 'text-danger font-weight-bold' : 'text-success font-weight-bold'">
            {{ currentNotification ? currentNotification.title : '' }}
            ({{ currentNotification.is_urgent ? 'Urgent' : 'Normal' }})
          </span>
        </div>
      </template>

      <div v-if="loadingNotification" class="text-center py-5">
        <h6>Please wait, loading notifications...</h6>
      </div>
      <div v-else-if="currentNotification" class="notification-card">
        <div class="notification-content mb-4" v-html="currentNotification.details"></div>
      </div>

      <template #modal-footer>
        <div class="w-100 d-flex justify-content text-center mt-0 text-muted" v-if="notifications.length > 1">
          {{ currentIndex + 1 }} of {{ notifications.length }}
        </div>
        <div class="w-100 d-flex justify-content-end mt-0">
          <b-button variant="secondary" size="xs" class="mx-1" @click="skipNotification">Skip</b-button>
          <b-button variant="primary" size="xs" class="mx-1" @click="markAsRead(currentNotification)">Mark as
            Read</b-button>
        </div>
      </template>
    </b-modal>

    <!-- Stat cards -->
    <div class="row stat-cards-row">
      <div class="col-lg-3 col-sm-6 mb-3" v-for="(b, i) in getDashboardcardDate.booking" :key="i">
        <dashboard-card :data="b"></dashboard-card>
      </div>
    </div>

    <!-- Submission status -->
    <div class="row" v-if="getDashboardcardDate.submission_status_this_month">
      <div class="col-md-6">
        <div class="panel-card">
          <div class="panel-card__header light">
            <span class="panel-card__title">
              <i class="fa fa-paper-plane-o"></i>
              Total Submissions This Month
            </span>
          </div>
          <div class="panel-card__body" style="padding: 0;">
            <table class="submission-table">
              <thead>
                <tr>
                  <th style="width:40%; padding-left: 20px;">Service</th>
                  <th class="cell-center" style="width:20%;">Transmitted</th>
                  <th class="cell-center" style="width:20%;">Submitted</th>
                  <th class="cell-center" style="width:20%; padding-right: 20px;">Error</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(data, i) in getDashboardcardDate.submission_status_this_month" :key="i">
                  <td style="padding-left: 20px;">{{ data.service }}</td>
                  <td class="cell-center">
                    <span :class="data.transmitted_count > 0 ? 'cell-link' : 'cell-muted'"
                      @click="data.transmitted_count > 0 ? toStoreForward(data, '200') : null">{{ data.transmitted_count
                      }}</span>
                  </td>
                  <td class="cell-center">
                    <span :class="data.submitted_count > 0 ? 'cell-link' : 'cell-muted'"
                      @click="data.submitted_count > 0 ? toStoreForward(data, '300') : null">{{ data.submitted_count
                      }}</span>
                  </td>
                  <td class="cell-center" style="padding-right: 20px;">
                    <span :class="data.error_count > 0 ? 'cell-error' : 'cell-muted'"
                      @click="data.error_count > 0 ? toStoreForward(data, 'error') : null">{{ data.error_count }}</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- Appointments -->
    <div class="panel-card">
      <div class="panel-card__header light">
        <span class="panel-card__title">
          <i class="fa fa-calendar"></i>
          Today's Appointments
        </span>
      </div>
      <div class="panel-card__body" style="padding: 0; overflow: hidden;">
        <div class="appt-panel appt-panel--full">
          <appointment-list :key="key" :app-type="false" :dash-type="'today'"></appointment-list>
        </div>
      </div>
    </div>

    <div class="panel-card">
      <div class="panel-card__header light">
        <span class="panel-card__title">
          <i class="fa fa-calendar-o"></i>
          Upcoming Appointments
        </span>
      </div>
      <div class="panel-card__body" style="padding: 0; overflow: hidden;">
        <div class="appt-panel appt-panel--full">
          <appointment-list :key="key" :app-type="false" :dash-type="'upcomming'"></appointment-list>
        </div>
      </div>
    </div>

    <!-- Charts -->
    <div class="section-header">
      <h2>Analytics</h2>
      <div class="chart-filters">
        <button class="filter-btn" :class="{ active: selectedBtn === 1 }" @click="getseriesdata(1)">Last Week</button>
        <button class="filter-btn" :class="{ active: selectedBtn === 2 }" @click="getseriesdata(2)">Last Month</button>
      </div>
    </div>

    <div class="panel-card">
      <div class="panel-card__header light">
        <span class="panel-card__title"><i class="fa fa-bar-chart"></i> Total Appointments</span>
      </div>
      <div class="panel-card__body">
        <v-data-loader v-if="getLoadingData"></v-data-loader>
        <line-chart v-else :key="index" :width="900" :ctype="'bar'" :options="options"
          :series="filtered_location"></line-chart>
      </div>
    </div>

    <div class="panel-card" v-if="currentUser.u.is_service_provider || currentUser.u.is_admin">
      <div class="panel-card__header light">
        <span class="panel-card__title">
          <i class="fa fa-bar-chart"></i>
          {{ currentUser.u.is_service_provider ? 'Appointments by Location' : 'Appointments by Service Provider' }}
        </span>
      </div>
      <div class="panel-card__body">
        <v-data-loader v-if="getLoadingData"></v-data-loader>
        <line-chart v-else :key="index" :width="900" :ctype="'bar'" :options="lineoptions"
          :series="lineseries"></line-chart>
      </div>
    </div>

    <div class="panel-card" v-if="!currentUser.u.is_admin">
      <div class="panel-card__header light">
        <span class="panel-card__title"><i class="fa fa-line-chart"></i> Services</span>
      </div>
      <div class="panel-card__body">
        <v-data-loader v-if="getLoadingData"></v-data-loader>
        <line-chart v-else :key="index" :width="900" :ctype="'line'" :options="serviceoptions"
          :series="serviceseries"></line-chart>
      </div>
    </div>

  </div>
</template>

<script>
import LineChart from "@/includes/graph/Chart";
import DashboardCard from "@/components/Dashboard/DashboardCard";
import vDataLoader from "@/components/frontend/spinner.vue";
import AppointmentList from "@/pages/backend/appointment/lists/index";
import { mapGetters } from "vuex";
import {
  GET_DASHBOARD_CARD_DATA,
  FETCH_DATA_FOR_GRAPH,
  MARK_AS_READ_NOTIFICATION
} from "@/core/services/store/actions.type";
import { SET_BREADCRUMB } from "@/core/services/store/mutations.type";

export default {
  components: { LineChart, DashboardCard, vDataLoader, AppointmentList },
  data() {
    let startDate = this.$moment().format("YYYY/MM/DD");
    let endDate = this.$moment().format("YYYY/MM/DD");
    return {
      breadCrumbs: [{ id: 1, title: "Dashboard", route: "" }],
      showNotificationModal: false,
      loadingNotification: true,
      notifications: [],
      currentIndex: 0,
      key: 0,
      getLoadingData: false,
      dateRange: { startDate, endDate },
      lineoptions: {
        chart: {
          height: 380,
          type: "bar",
          stacked: true,
          stackType: "normal",
          toolbar: { show: false },
          zoom: { enabled: false }
        },
        colors: [],
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: "55%",
            borderRadius: 3
          }
        },
        dataLabels: { enabled: false },
        xaxis: { categories: [], title: { text: "Date" } },
        yaxis: { title: { text: "Appointments" } },
        legend: {
          position: "bottom",
          horizontalAlign: "center",
          offsetY: 6
        },
        fill: { opacity: 1 },
        tooltip: {
          shared: true,
          intersect: false,
          y: { formatter: val => `${val} appointments` }
        }
      },
      options: {
        chart: { height: 350, type: "bar", dropShadow: { enabled: true, top: 18, left: 7, blur: 10, opacity: 0.2 }, zoom: { enabled: false } },
        colors: ["#1565c0"],
        dataLabels: { enabled: false },
        stroke: { curve: "straight", width: 3 },
        markers: { size: 1 },
        xaxis: { categories: [], title: { text: "Date" } },
        legend: { position: "bottom", horizontalAlign: "right" }
      },
      series: [],
      lineseries: [],
      selectedBtn: 2,
      app_radio: "total",
      selected_location: [],
      index: 0,
      serviceoptions: {
        chart: { height: 350, type: "line", dropShadow: { enabled: true, top: 18, left: 7, blur: 10, opacity: 0.2 }, zoom: { enabled: false } },
        colors: [],
        dataLabels: { enabled: false },
        stroke: { curve: "straight", width: 3 },
        markers: { size: 1 },
        xaxis: { categories: [], title: { text: "Date" } },
        legend: { position: "bottom", horizontalAlign: "right" }
      },
      serviceseries: []
    };
  },
  computed: {
    ...mapGetters(["currentUser", "getDashboardcardDate"]),
    filtered_location() {
      if (this.app_radio === "total") return this.series;
      return this.series.filter(loc => this.selected_location.includes(loc.name));
    },
    currentNotification() {
      return this.notifications[this.currentIndex] || null;
    }
  },
  created() {
    this.$store.commit(SET_BREADCRUMB, this.breadCrumbs);
    this.$store.dispatch(GET_DASHBOARD_CARD_DATA, {
      type: this.currentUser.u.is_admin ? "admin" : this.currentUser.u.is_client ? "c" : "sp",
      booking: { booking: this.booking }
    });
    this.getseriesdata();
    if (!localStorage.getItem('showNotificationModal') && (this.currentUser.u.is_service_provider || this.currentUser.u.is_client)) {
      this.$watch(
        () => this.getDashboardcardDate.notifications,
        newVal => {
          if (newVal && newVal.length > 0) {
            this.notifications = newVal;
            this.loadingNotification = false;
            this.showNotificationModal = true;
            localStorage.setItem('showNotificationModal', JSON.stringify(true));
          }
        },
        { immediate: true }
      );
    }
  },
  methods: {
    toStoreForward(data, type) {
      if (data.id == null) return;
      const now = new Date();
      const start_date = new Date(now.getFullYear(), now.getMonth(), 2);
      const end_date = new Date(now.getFullYear(), now.getMonth() + 1, 1);
      const format = d => d.toISOString().split('T')[0];
      const params = { service_id: data.id, service_status: type, start_date: format(start_date), end_date: format(end_date) };
      this.$router.push({ name: this.currentUser.u.is_admin ? 'admin.storeforward' : 'client.storeforward', params });
    },
    getseriesdata(val = 1) {
      this.getseriesdatatotal(val);
    },
    setdate(val) {
      let startDate = new Date(), endDate = new Date();
      if (val === 1) {
        startDate = this.$moment(new Date()).add(-6, "days");
        endDate = this.$moment(new Date());
      } else if (val === 2) {
        startDate = this.$moment(new Date()).add(-30, "days");
        endDate = this.$moment(new Date());
      }
      this.dateRange = { startDate, endDate };
    },
    getseriesdatatotal(val = 1) {
      this.selectedBtn = val;
      this.setdate(val);
      this.series = [];
      this.getLoadingData = true;
      this.$store
        .dispatch(FETCH_DATA_FOR_GRAPH, {
          daterange: this.dateRange,
          type: val,
          client: this.currentUser.u.is_admin ? "admin" : this.currentUser.u.is_client ? "c" : "sp"
        })
        .then(res => {
          const [location] = res.details;

          this.options = {
            ...this.options,
            xaxis: { ...this.options.xaxis, categories: location.location_total_detail.date }
          };
          this.series = [{ name: "Total", data: location.location_total_detail.count }];

          if (location.location_wise_detail) {
            this.lineoptions = {
              ...this.lineoptions,
              colors: location.location_wise_detail.location_colors,
              xaxis: { ...this.lineoptions.xaxis, categories: location.location_total_detail.date }
            };
            this.lineseries = location.location_wise_detail.detail;
          }

          this.serviceoptions = {
            ...this.serviceoptions,
            colors: location.service_details.service_colors,
            xaxis: { ...this.serviceoptions.xaxis, categories: location.service_details.date }
          };
          this.serviceseries = location.service_details.detail;

          this.index += 1;
          this.getLoadingData = false;
        })
        .catch(() => {
          this.$toastr.e("oops! something went wrong");
          this.getLoadingData = false;
          this.index += 1;
        });
    },
    skipNotification() { this.nextNotification(); },
    markAsRead(n) {
      this.nextNotification();
      this.$store.dispatch(MARK_AS_READ_NOTIFICATION, { notification_id: n.id });
    },
    nextNotification() {
      if (this.currentIndex < this.notifications.length - 1) this.currentIndex++;
      else this.showNotificationModal = false;
    }
  }
};
</script>
